'use client'

import { useState, useTransition } from "react"
import { useRouter } from "next/navigation"
import { ArrowRight, Check, ShoppingCart } from "lucide-react"

import { Button } from "@/components/elements/Button"
import DomainName from "@/components/domains/DomainName"
import { useDomainCart, CART_MAX_ITEMS, checkoutBlocker } from "@/hooks/use-domain-cart"
import { toast } from "@/lib/toast"
import { formatPrice } from "@/lib/util"
import type { DomainSearchResult } from "@/lib/request"

/**
 * The secondary TLDs beneath a `.mn` verdict - search mode's hero card and
 * every AI suggestion's default row both use it the same way.
 *
 * `.mn` is the headline everywhere this appears, so these rows stay quiet on
 * purpose: smaller type than the verdict they sit under, no backdrop glow of
 * their own, one line each. There is no term selector per row - one on each of
 * seventeen rows would turn a short list into a form. Rows inherit the term
 * chosen once on the verdict card above, and the prices arrive already priced
 * at it by the server, because registries price the first year promotionally
 * often enough that multiplying a 1-year figure would be wrong.
 *
 * Each row carries the same two actions as the verdict card - collect, or buy
 * outright - because a visitor who came for a `.com` should not have to route
 * through a basket to buy one. Buying is the labelled one: across seventeen
 * rows a repeated "Сагсанд нэмэх" crowded the name off the line on a phone and
 * drowned out the action most visitors actually want, so the basket keeps the
 * icon and gives up its label.
 *
 * No card chrome here on purpose: search mode and AI mode each already sit
 * inside their own surface (the page background under the verdict card, the
 * AI suggestion's `main-card` panel), so this renders only the heading and the
 * rows and lets the caller decide what wraps them.
 */
export default function TldResultList({ options, period = 1, signedIn = false, danVerified = false, title = 'Бусад өргөтгөл' }: { options: DomainSearchResult[], period?: number, signedIn?: boolean, danVerified?: boolean, title?: string }) {
  const cart = useDomainCart()
  // Above the early return: hooks cannot run conditionally.
  const router = useRouter()
  const [navPending, startNavTransition] = useTransition()
  // Which row was clicked, so only that button shows the spinner rather than
  // every row in the list.
  const [busyDomain, setBusyDomain] = useState<string | null>(null)

  if(!options || options.length === 0) return null

  const addToCart = (domain: string, allowedYears: number[] | null) => {
    const res = cart.add(domain, period, allowedYears)
    if(!res.ok && res.reason === 'full') return toast.error(`Сагсанд хамгийн ихдээ ${CART_MAX_ITEMS} домэйн нэр багтана`)
    toast.success('Сагсанд нэмэгдлээ')
  }

  // Same shape as the verdict card's primary. The name goes into the basket
  // either way — so /checkout reads one source of truth, and a detour through
  // login or ДАН comes back to a basket that still holds it (localStorage).
  const buyNow = (domain: string, allowedYears: number[] | null) => {
    const res = cart.add(domain, period, allowedYears)
    // A full basket is the one case where we must not navigate: the name was
    // not saved, so arriving at checkout would find it missing.
    if(!res.ok && res.reason === 'full') return toast.error(`Сагсанд хамгийн ихдээ ${CART_MAX_ITEMS} домэйн нэр багтана`)
    const blocker = checkoutBlocker({ signedIn, danVerified })
    // /checkout is a server render, so the click needs to show progress rather
    // than sit inert for the round trip.
    setBusyDomain(domain)
    startNavTransition(() => { router.push(blocker || '/checkout') })
  }

  return (
    <div>
      <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
      <ul className="mt-2 divide-y divide-border/60">
        {/* Rows stack below sm and sit on one line from sm up. On a 360px phone
            the price and two actions leave the name so little room that
            `DomainName`'s `break-all` splits it mid-word ("sanchir maaa.c om"),
            which makes the list unreadable. Giving the name its own full-width
            row means it never has to break, and price + actions share the row
            underneath. */}
        { options.map(option => (
          <li key={option.domain} className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:py-2.5">
            <div className="min-w-0">
              <DomainName domain={option.domain} size="base" className={option.available ? '' : 'text-muted-foreground'} />
              { !option.available && option.reason ? (
                <p className="text-xs text-muted-foreground mt-0.5">{option.reason}</p>
              ) : null }
            </div>

            { option.available ? (
              <div className="flex items-center justify-between gap-3 sm:justify-end sm:shrink-0">
                {/* Campaigns apply to these TLDs too (only the .mn standing sale
                    is gated), so a discounted row must say so rather than just
                    quoting a lower number with no explanation. Same struck-through
                    treatment the .mn hero card uses, at row scale. The price stays
                    visible once the name is in the basket - it is the number the
                    row exists to communicate, and hiding it there made a collected
                    row say less than an uncollected one. */}
                <span className="tabular-nums">
                  { option.totalAmount !== option.amount && (
                    <span className="mr-2 text-xs text-muted-foreground/70 line-through">{formatPrice(option.amount)}</span>
                  )}
                  {/* The number this row exists to communicate, so it is set in
                      the display face at full strength — the same treatment the
                      verdict card gives its price, one step down in size. The
                      struck-through original stays small and muted so the gap
                      between them reads as hierarchy rather than two competing
                      figures. */}
                  <span className="font-gilroy font-semibold tracking-tight text-base sm:text-lg text-foreground">{formatPrice(option.totalAmount)}</span>
                </span>

                <div className="flex items-center gap-2">
                  {/* Icon-only: seventeen rows each repeating "Сагсанд нэмэх"
                      crowded the name off the line on a phone and drowned out the
                      one action most visitors actually want. The basket is the
                      secondary path, so it keeps the icon and gives up the label;
                      the name of the action lives in `aria-label` and the tooltip
                      so it is still announced and still discoverable on hover. */}
                  { cart.has(option.domain) ? (
                    <Button
                      tag="button"
                      variant="outline"
                      size="sm"
                      className="text-xs!"
                      isActive={false}
                      title="Сагсанд байна"
                      aria-label={`${option.domain} сагсанд байна`}
                    >
                      <Check className="size-3.5" aria-hidden="true" />
                    </Button>
                  ) : (
                    <Button
                      tag="button"
                      variant="outline"
                      size="sm"
                      className="text-xs!"
                      title="Сагсанд нэмэх"
                      aria-label={`${option.domain} сагсанд нэмэх`}
                      onClick={() => addToCart(option.domain, option.allowedYears)}
                    >
                      <ShoppingCart className="size-3.5" aria-hidden="true" />
                    </Button>
                  )}

                  <Button
                    tag="button"
                    size="sm"
                    className="text-xs!"
                    aria-label={`${option.domain} авах`}
                    isLoading={navPending && busyDomain === option.domain}
                    loadingMessage=" "
                    onClick={() => buyNow(option.domain, option.allowedYears)}
                  >
                    Авах
                    <ArrowRight className="size-3.5" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            ) : null }
          </li>
        )) }
      </ul>
    </div>
  )
}
